import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  Text,
  Box,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  saveUserReportTemplate,
  updateUserReportTemplate,
  saveUserDefaultReportTemplate,
  loadSavedUserReportTemplates,
  REPORT_TYPE_LABELS,
} from "../utils/reportProcessingUtils";

/**
 * Shared Process Report Prompt Editor modal component.
 * Used by PatientPage, EditableDocumentPage, and SchedulesTable.
 *
 * Props:
 *   isOpen           - whether the modal is visible
 *   onClose          - called when the modal is closed
 *   onRun({ fullPrompt, editablePrompt, templateInfo }) - called when the user clicks Run
 *   reportType       - one of REPORT_TYPES constants ("PFT", "CXR", "CBC", "SLEEP_STUDY")
 *   initialData      - output of prepareReportProcessingData(): { reportType, reportTypeLabel, templateInfo, editablePrompt, savedTemplates, fileName }
 *   toast            - Chakra UI toast function
 */
const ProcessReportPromptEditor = ({ isOpen, onClose, onRun, reportType, initialData, toast }) => {
  const [editablePrompt, setEditablePrompt] = useState("");
  const [templateInfo, setTemplateInfo] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [originalTemplate, setOriginalTemplate] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Initialize state from initialData when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setEditablePrompt(initialData.editablePrompt || "");
      setTemplateInfo(initialData.templateInfo || "");
      setOriginalPrompt(initialData.editablePrompt || "");
      setOriginalTemplate(initialData.templateInfo || "");
      setDefaultPrompt(initialData.editablePrompt || "");
      setDefaultTemplate(initialData.templateInfo || "");
      setSavedTemplates(initialData.savedTemplates || []);
      setSelectedTemplateIndex(-1);
      setShowSaveDialog(false);
    }
  }, [isOpen, initialData]);

  const hasChanges = () => {
    return editablePrompt !== originalPrompt || templateInfo !== originalTemplate;
  };

  const handleClose = () => {
    setSelectedTemplateIndex(-1);
    onClose();
  };

  const handleRunClick = () => {
    if (!editablePrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasChanges()) {
      setShowSaveDialog(true);
      return;
    }

    proceedWithRun();
  };

  const proceedWithRun = () => {
    const fullPrompt = templateInfo.trim()
      ? `${editablePrompt}\n\n${templateInfo}`
      : editablePrompt;
    setShowSaveDialog(false);
    handleClose();
    onRun({ fullPrompt, editablePrompt, templateInfo });
  };

  const handleSaveAsNewAndRun = async () => {
    try {
      await saveUserReportTemplate(reportType, templateInfo, editablePrompt);
      toast({
        title: "Template Saved",
        description: "Your template and prompt have been saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      const saved = await loadSavedUserReportTemplates(reportType);
      setSavedTemplates(saved);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error saving report template:", error);
      toast({
        title: "Save Error",
        description: `Failed to save template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateCurrentAndRun = async () => {
    try {
      if (selectedTemplateIndex < 0) {
        throw new Error("No template selected to update");
      }

      await updateUserReportTemplate(reportType, templateInfo, editablePrompt, selectedTemplateIndex);
      toast({
        title: "Template Updated",
        description: "Your template and prompt have been updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      const saved = await loadSavedUserReportTemplates(reportType);
      setSavedTemplates(saved);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error updating report template:", error);
      toast({
        title: "Update Error",
        description: `Failed to update template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveAsDefaultAndRun = async () => {
    try {
      await saveUserDefaultReportTemplate(reportType, templateInfo, editablePrompt);
      toast({
        title: "Default Saved",
        description: "Your personal default template and prompt have been saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setDefaultTemplate(templateInfo);
      setDefaultPrompt(editablePrompt);
      setOriginalTemplate(templateInfo);
      setOriginalPrompt(editablePrompt);

      setShowSaveDialog(false);
      proceedWithRun();
    } catch (error) {
      console.error("Error saving default report template:", error);
      toast({
        title: "Save Error",
        description: `Failed to save default template: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTabChange = (index) => {
    if (index === 0) {
      const promptToUse = defaultPrompt || originalPrompt;
      const templateToUse = defaultTemplate || originalTemplate;
      setEditablePrompt(promptToUse);
      setTemplateInfo(templateToUse);
      setSelectedTemplateIndex(-1);
      setOriginalPrompt(promptToUse);
      setOriginalTemplate(templateToUse);
    } else {
      const templateIndex = index - 1;
      const template = savedTemplates[templateIndex];
      if (template) {
        setEditablePrompt(template.prompt);
        setTemplateInfo(template.template);
        setSelectedTemplateIndex(templateIndex);
        setOriginalPrompt(template.prompt);
        setOriginalTemplate(template.template);
      }
    }
  };

  const reportLabel = REPORT_TYPE_LABELS[reportType] || reportType || "Report";

  const renderTabContent = () => (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.700">
          Prompt (You can modify this):
        </Text>
        <Textarea
          value={editablePrompt}
          onChange={(e) => setEditablePrompt(e.target.value)}
          placeholder="Enter your prompt here..."
          rows={6}
          resize="vertical"
          fontFamily="mono"
          fontSize="sm"
        />
      </Box>

      <Box>
        <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.700">
          Template (Editable):
        </Text>
        <Textarea
          value={templateInfo}
          onChange={(e) => setTemplateInfo(e.target.value)}
          placeholder="Enter output template here (optional)..."
          rows={10}
          resize="vertical"
          fontFamily="mono"
          fontSize="sm"
        />
      </Box>
    </VStack>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" blockScrollOnMount={false}>
        <ModalOverlay style={{ zIndex: 10000 }} />
        <ModalContent maxH="90vh" overflowY="auto" containerProps={{ zIndex: 10001 }}>
          <ModalHeader>Process Report: {reportLabel}</ModalHeader>
          <ModalCloseButton />
          <Box px={6} pt={2} pb={2} borderBottomWidth="1px" display="flex" alignItems="center" gap={3}>
            <Button
              colorScheme="blue"
              onClick={handleRunClick}
              isDisabled={!editablePrompt.trim()}
            >
              Run Process Report
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
          <ModalBody>
            {initialData?.fileName && (
              <Text fontSize="sm" color="gray.500" mb={3}>
                File: <strong>{initialData.fileName}</strong>
              </Text>
            )}
            <Tabs
              index={selectedTemplateIndex === -1 ? 0 : selectedTemplateIndex + 1}
              onChange={handleTabChange}
            >
              <TabList>
                <Tab>Default</Tab>
                {savedTemplates.map((template, index) => (
                  <Tab key={index}>{template.name}</Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel>{renderTabContent()}</TabPanel>
                {savedTemplates.map((template, index) => (
                  <TabPanel key={index}>{renderTabContent()}</TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={showSaveDialog}
        leastDestructiveRef={undefined}
        onClose={() => setShowSaveDialog(false)}
        isCentered
      >
        <AlertDialogOverlay style={{ zIndex: 10002 }}>
          <AlertDialogContent maxW="550px" style={{ zIndex: 10003 }}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Save Template Changes?
            </AlertDialogHeader>
            <AlertDialogBody>
              You've made changes to the template or prompt. How would you like to save these
              changes?
            </AlertDialogBody>
            <AlertDialogFooter flexWrap="wrap" gap={2}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSaveDialog(false);
                  proceedWithRun();
                }}
              >
                Don't Save
              </Button>
              {selectedTemplateIndex >= 0 && (
                <Button
                  colorScheme="green"
                  onClick={handleUpdateCurrentAndRun}
                  fontSize="sm"
                >
                  Update Current & Run
                </Button>
              )}
              <Button colorScheme="blue" onClick={handleSaveAsNewAndRun} fontSize="sm">
                Save as New & Run
              </Button>
              <Button colorScheme="purple" onClick={handleSaveAsDefaultAndRun} fontSize="sm">
                Save as new Default
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ProcessReportPromptEditor;
